import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { AlvenariaEstruturalLista } from "../../../../domain/models/plano_alvenaria_estrutural_lista";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { DefaultFormCheckBox } from "../../../../../../common/components/default_form_checkbox";
import { IconButton, styled } from "@mui/material";
import { ObraAlvenariaEstruturalForm, ObraAlvenariaEstruturalSchema } from "../../../schemas/Obras_alvenaria_estrutura/obra_alvenaria_estrutural";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { ChangeEvent, useEffect } from "react";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// AlvenariaEstruturaModal

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedAlvenariaEstrutura: AlvenariaEstruturalLista | undefined,
  onClose: () => void;
  onSubmit: (e: ObraAlvenariaEstruturalForm) => void;
  handleDownload: (idAlvenariaObra: number, nroArquivo: number, nomeArquivo: string) => void;
};

export const AlvenariaEstruturalModal = ({
  open,
  saving,
  loading,
  selectedAlvenariaEstrutura,
  onClose,
  onSubmit,
  handleDownload,
}: Props) => {
  const hookForm = useForm<ObraAlvenariaEstruturalForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ObraAlvenariaEstruturalSchema),
  });

  const [arquivo1, arquivo2, limparArquivo1, limparArquivo2] = hookForm
    .watch(['Arquivo1', 'Arquivo2', 'limparArquivo1', 'limparArquivo2']);

  const requestArquivo1Upload = limparArquivo1 !== true && (arquivo1 === null || arquivo1 === undefined);
  const requestArquivo2Upload = limparArquivo2 !== true && (arquivo2 === null || arquivo2 === undefined);

  useEffect(() => {
    if (open) {
      if (selectedAlvenariaEstrutura) {
        if (selectedAlvenariaEstrutura.DataRecebimento) {
          hookForm.setValue('DataRecebimento', new Date(selectedAlvenariaEstrutura.DataRecebimento));
        } else {
          hookForm.resetField('DataRecebimento');
        }

        if (selectedAlvenariaEstrutura.DataEnvio) {
          hookForm.setValue('DataEnvio', new Date(selectedAlvenariaEstrutura.DataEnvio));
        } else {
          hookForm.resetField('DataEnvio');
        }

        hookForm.setValue('Aprovado', selectedAlvenariaEstrutura.Aprovado === true ? 1 : selectedAlvenariaEstrutura.Aprovado === false ? 0 : 0);
        hookForm.setValue('Dimensao', selectedAlvenariaEstrutura.Dimensao);
        hookForm.setValue('TipoBloco', selectedAlvenariaEstrutura.TipoBloco);
        hookForm.setValue('NroNotaFiscal', selectedAlvenariaEstrutura.NroNotaFiscal);
        hookForm.setValue('Fornecedor', selectedAlvenariaEstrutura.Fornecedor);
        hookForm.setValue('LocalAplicacao', selectedAlvenariaEstrutura.LocalAplicacao);
        hookForm.setValue('NomeLaboratorio', selectedAlvenariaEstrutura.NomeLaboratorio);
        hookForm.setValue('EnsaiosSolicitados', selectedAlvenariaEstrutura.EnsaiosSolicitados);
        hookForm.setValue('NrCorpoProva', selectedAlvenariaEstrutura.NrCorpoProva);
        hookForm.setValue('NrAmostras', selectedAlvenariaEstrutura.NrAmostras);
        hookForm.setValue('Ensaio1', selectedAlvenariaEstrutura.Ensaio1);
        hookForm.setValue('Ensaio2', selectedAlvenariaEstrutura.Ensaio2);
        hookForm.setValue('NroAmostras1', selectedAlvenariaEstrutura.NroAmostras1);
        hookForm.setValue('NroAmostras2', selectedAlvenariaEstrutura.NroAmostras2);
        hookForm.setValue('EspecificacaoArgamassa', selectedAlvenariaEstrutura.EspecificacaoArgamassa);
        hookForm.setValue('RbIdade1', selectedAlvenariaEstrutura.RbIdade1);
        hookForm.setValue('RbIdade2', selectedAlvenariaEstrutura.RbIdade2);
        hookForm.setValue('RbIdade3', selectedAlvenariaEstrutura.RbIdade3);
        hookForm.setValue('RbResultado1', selectedAlvenariaEstrutura.RbResultado1);
        hookForm.setValue('RbResultado2', selectedAlvenariaEstrutura.RbResultado2);
        hookForm.setValue('RbResultado3', selectedAlvenariaEstrutura.RbResultado3);
        hookForm.setValue('RaIdade1', selectedAlvenariaEstrutura.RaIdade1);
        hookForm.setValue('RaResultado1', selectedAlvenariaEstrutura.RaResultado1);
        hookForm.setValue('RaIdade2', selectedAlvenariaEstrutura.RaIdade2);
        hookForm.setValue('RaResultado2', selectedAlvenariaEstrutura.RaResultado2);
        hookForm.setValue('RaIdade3', selectedAlvenariaEstrutura.RaIdade3);
        hookForm.setValue('RaResultado3', selectedAlvenariaEstrutura.RaResultado3);
        hookForm.setValue('ResponsavelCadastrado', selectedAlvenariaEstrutura.ResponsavelCadastrado);
        hookForm.setValue('ResistenciaSolicitada', selectedAlvenariaEstrutura.ResistenciaSolicitada);
        hookForm.setValue('QtdeRecibida', Number(selectedAlvenariaEstrutura.QtdeRecibida));
        hookForm.setValue('funcionarioNome', selectedAlvenariaEstrutura.FuncionarioNome);
        hookForm.setValue('DescResponsavel', selectedAlvenariaEstrutura.DescResponsavel);
        hookForm.setValue("IdFuncionario", selectedAlvenariaEstrutura.IdFuncionario);
        hookForm.setValue("Observacao", selectedAlvenariaEstrutura.Observacao);
        hookForm.setValue("limparArquivo1", false);
        hookForm.setValue("limparArquivo2", false);
        hookForm.setValue("Arquivo1", null);
        hookForm.setValue("Arquivo2", null);
      } else {
        hookForm.reset({ limparArquivo1: false, limparArquivo2: false });
      }
    }
  }, [
    open,
    selectedAlvenariaEstrutura,
    hookForm,
  ]);


  return (
    <BaseFormControlledModal
      width="1200px"
      open={open}
      saving={saving}
      loading={loading}
      title={'Adicionar Nova Alvenaria Estrutura'}
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <FormContainer title={"Informações da Entrega"} arrangement="fullRow">
        <>
          <div className="flex flex-row gap-x-8">
            <FormDatePickerInput
              name='DataRecebimento'
              label='Data Recebimento'
              required={true}
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='Dimensao'
              label='Dimensao'
              reactHookForm={hookForm} />

          </div>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='TipoBloco'
              label='Tipo Bloco'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='NroNotaFiscal'
              label='Nº Nota Fiscal'
              reactHookForm={hookForm} />

          </div>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='ResistenciaSolicitada'
              label='Resistência Solicitada'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='QtdeRecibida'
              label='Quantidade Recebida'
              reactHookForm={hookForm} />
          </div>
          <DefaultFormInput
            name='Fornecedor'
            label='Fornecedor'
            reactHookForm={hookForm} />


          <DefaultFormInput
            name='LocalAplicacao'
            label='Local Aplicacao'
            multiline={true}
            minRows={4}
            reactHookForm={hookForm} />
        </>
      </FormContainer>
      <FormContainer title={"Dados do Ensaio"} arrangement="fullRow">
        <>
          <DefaultFormInput
            name='NomeLaboratorio'
            label='Nome Laboratorio'
            reactHookForm={hookForm} />

          <DefaultFormInput
            name='EnsaiosSolicitados'
            label='EnsaiosSolicitados'
            reactHookForm={hookForm} />

          <div className="flex flex-row gap-x-8">
            <FormDatePickerInput
              name='DataEnvio'
              label='Data Envio'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='NrCorpoProva'
              label='Nº Corpo de Prova'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='NrAmostras'
              label='Nº de amostras'
              reactHookForm={hookForm} />
          </div>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='Ensaio1'
              label='Ensaio 1'
              reactHookForm={hookForm} />
            <DefaultFormInput
              name='NroAmostras1'
              label='Nº amostras'
              reactHookForm={hookForm} />
          </div>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='Ensaio2'
              label='Ensaio 2'
              reactHookForm={hookForm} />
            <DefaultFormInput
              name='NroAmostras2'
              label='Nº amostras'
              reactHookForm={hookForm} />
          </div>
          <DefaultFormInput
            name='EspecificacaoArgamassa'
            label='Especificação da argamassa de assentamento'
            multiline={true}
            minRows={4}
            reactHookForm={hookForm} />
        </>
      </FormContainer>
      <FormContainer title={"Dados do Ensaio"} arrangement="fullRow">
        <>
          <h2 className="text-lg font-bold text-center mb-4">Resultado Bloco</h2>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='RbIdade1'
              label='Idade 1'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='RbResultado1'
              label='Resultado 1'
              reactHookForm={hookForm} />
          </div>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='RbIdade2'
              label='Idade 2'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='RbResultado2'
              label='Resultado 2'
              reactHookForm={hookForm} />
          </div>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='RbIdade3'
              label='Idade 3'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='RbResultado3'
              label='Resultado 3'
              reactHookForm={hookForm} />
          </div>
          <h2 className="text-lg font-bold text-center mb-4">Resultado Argamassa</h2>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='RaIdade1'
              label='Idade 1'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='RaResultado1'
              label='Resultado 1'
              reactHookForm={hookForm} />
          </div>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='RaIdade2'
              label='Idade 2'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='RaResultado2'
              label='Resultado 2'
              reactHookForm={hookForm} />
          </div>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='RaIdade3'
              label='Idade 3'
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='RaResultado3'
              label='Resultado 3'
              reactHookForm={hookForm} />
          </div>
          <FormRadioGroupInput
            name='Aprovado'
            label='Aprovado'
            options={[
              { id: 1, label: 'Sim' },
              { id: 0, label: 'Não' },
            ]}
            reactHookForm={hookForm} />
          <div className="flex flex-row gap-x-8">
            <DefaultFormCheckBox name='ResponsavelCadastrado' label='Preencher o responsável' reactHookForm={hookForm} />

            <div className="flex-1">
              {hookForm.watch("ResponsavelCadastrado") === true ? (
                <DefaultFormInput name='DescResponsavel' label='Responsável Pela Aprovação' reactHookForm={hookForm} />
              ) :
                <DropDownFuncionario name="IdFuncionario" readOnly={saving} reactHookForm={hookForm} label="Responsável Pela Aprovação" />
              }
            </div>
          </div>
          <DefaultFormInput
            name='Observacao'
            label='Observação'
            multiline={true}
            minRows={4}
            reactHookForm={hookForm} />
          <div className="flex flex-row gap-x-8">
            <div className="flex-1">
              <Controller
                name='Arquivo1'
                control={hookForm.control}
                render={({ field }) => (selectedAlvenariaEstrutura && selectedAlvenariaEstrutura.NomeArquivo1 &&
                  selectedAlvenariaEstrutura.NomeArquivo1.length > 0 && requestArquivo1Upload) ? (
                  <div className="w-full flex flex-row justify-between items-center">
                    <span className="cursor-pointer" onMouseDown={() => {
                      if (selectedAlvenariaEstrutura) {
                        handleDownload(
                          selectedAlvenariaEstrutura.IdAlvenariaObra, 1,
                          selectedAlvenariaEstrutura.NomeArquivo1 ?? '',
                        );
                      }
                    }}>
                      {selectedAlvenariaEstrutura?.NomeArquivo1 && selectedAlvenariaEstrutura.NomeArquivo1.length > 40
                        ? `${selectedAlvenariaEstrutura.NomeArquivo1.substring(0, 40)}...`
                        : selectedAlvenariaEstrutura?.NomeArquivo1}
                    </span>
                    <IconButton onClick={() => {
                      if (window.confirm('Você deseja excluir este arquivo?')) {
                        hookForm.setValue('limparArquivo1', true);
                      }
                    }}>
                      <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                    </IconButton>
                  </div>
                ) : (
                  <PrimaryButton
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    disabled={saving}
                    startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                    {field.value ? field.value.name : "Escolher arquivo"}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          field.onChange(file);
                          hookForm.setValue('limparArquivo1', false);
                        }
                      }}
                      multiple={false} />
                  </PrimaryButton>
                )}
              />
            </div>

            <div className="flex-1">
              <Controller
                name='Arquivo2'
                control={hookForm.control}
                render={({ field }) => (selectedAlvenariaEstrutura && selectedAlvenariaEstrutura.NomeArquivo2 &&
                  selectedAlvenariaEstrutura.NomeArquivo2.length > 0 && requestArquivo2Upload) ? (
                  <div className="w-full flex flex-row justify-between items-center">
                    <span className="cursor-pointer" onMouseDown={() => {
                      if (selectedAlvenariaEstrutura) {
                        handleDownload(
                          selectedAlvenariaEstrutura.IdAlvenariaObra, 2,
                          selectedAlvenariaEstrutura.NomeArquivo2 ?? '',
                        );
                      }
                    }}>
                      {selectedAlvenariaEstrutura.NomeArquivo2 && selectedAlvenariaEstrutura.NomeArquivo2.length > 40
                        ? `${selectedAlvenariaEstrutura.NomeArquivo2.substring(0, 40)}...`
                        : selectedAlvenariaEstrutura.NomeArquivo2}
                    </span>
                    <IconButton onClick={() => {
                      if (window.confirm('Você deseja excluir este arquivo?')) {
                        hookForm.setValue('limparArquivo2', true);
                      }
                    }}>
                      <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                    </IconButton>
                  </div>
                ) : (
                  <PrimaryButton
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    disabled={saving}
                    startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                    {field.value ? field.value.name : "Escolher arquivo"}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          field.onChange(file);
                          hookForm.setValue('limparArquivo2', false);
                        }
                      }}
                      multiple={false} />
                  </PrimaryButton>
                )}
              />
            </div>
          </div>
        </>
      </FormContainer>
    </BaseFormControlledModal>
  );
}